<template>
  <v-card class="my-5" max-width="600">
    <v-img
      class="white--text align-end"
      height="100px"
      min-width="200px"
      gradient="to top right, rgba(30, 30, 30, 0.58), rgba(30, 30, 30, 0.58)"
      :src="thgImpactImage(myThg.impactType).src"
    >
      <v-card-title>
        Kalenderjahr: {{ myThg.year }} <br />
        {{ myThg.numberplate + " " + thgImpactImage(myThg.impactType).smiley }}
        <v-spacer></v-spacer>
      </v-card-title>
    </v-img>

    <v-alert v-if="myThg.promotionConfiguration" type="success">
      <template #prepend>
        <v-icon large> mdi-gift </v-icon>
      </template>
      <div align="end">
        <span class="ml-1">
          {{ $t(`common.PromotionTypeEnum.${myThg.promotionConfiguration.type}`).toUpperCase() }}
        </span>
        <br />
        <span class="ml-1"> {{ promotionValue }} </span>
      </div>
    </v-alert>

    <v-card-subtitle class="pb-0">
      {{ $t("components.thg.ThgVehicleCard.subTitle", { date: dateSimple }) }}
    </v-card-subtitle>
    <v-card-text class="text--primary">
      {{ $t(thgStatus(myThg.status).text) }}
    </v-card-text>
    <v-row v-if="requestEmissionCalculationData(myThg.status)">
      <v-col class="ma-4">
        <thg-emission-calculation-dialog :thg="myThg" />
      </v-col>
    </v-row>
    <v-divider />
    <v-row v-if="isQuickCheckoutEnabled && myThg.quickCheckoutEnabledFor && myThg.quickCheckoutEnabledFor.length > 0">
      <v-col cols="12">
        <thg-quick-checkout :thg="myThg" class="ma-4" @refresh="refresh" />
      </v-col>
    </v-row>
    <v-divider
      v-if="isQuickCheckoutEnabled && myThg.quickCheckoutEnabledFor && myThg.quickCheckoutEnabledFor.length > 0"
    />

    <v-row>
      <v-col v-if="cashImpactInvestment">
        <v-card-title class="text--primary">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">{{ $t("components.thg.ThgVehicleCard.toolTip") }}</div>
            </template>
            <span>{{ $t(thgImpactImage(myThg.impactType).descriptionAlt) }}</span>
          </v-tooltip>
        </v-card-title>
        <v-progress-circular
          class="mx-4 mb-4"
          v-if="cashImpactInvestment"
          :size="100"
          :rotate="-90"
          :width="15"
          :value="cashImpactInvestment"
          color="green"
        >
          {{ cashImpactInvestment }} %*
        </v-progress-circular>
      </v-col>
      <v-col v-if="cashPayment">
        <v-card-title class="text--primary">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">{{ $t("components.thg.ThgVehicleCard.payout") }}</div>
            </template>
            <span>{{ $t("components.thg.ThgVehicleCard.payoutToolTip") }}</span>
          </v-tooltip>
        </v-card-title>
        <v-progress-circular class="mx-4 mb-4" :size="100" :rotate="-90" :width="15" :value="cashPayment" color="blue">
          {{ cashPayment }} %*
        </v-progress-circular>
      </v-col>
      <v-col v-if="customerAccount">
        <v-card-text class="text--primary mt-10">{{ $t("components.thg.ThgVehicleCard.customerAccount") }}</v-card-text>
      </v-col>
      <v-col v-if="!cashPayment && !customerAccount">
        <v-card-text class="text--primary mt-10">{{ $t("components.thg.ThgVehicleCard.impactAlt") }}</v-card-text>
      </v-col>
      <v-col v-if="!cashImpactInvestment && !customerAccount">
        <v-card-text class="text--primary mt-10">{{ $t("components.thg.ThgVehicleCard.noImpact") }}</v-card-text>
      </v-col>
    </v-row>
    <v-divider />
    <v-card-title class="text--primary">
      <div>{{ $t("components.thg.ThgVehicleCard.registration") }}</div>
      <v-spacer />
      <thg-quote-vehicle-update-registration-dialog v-if="showRegistrationUpdateDialog" :selectedThg="myThg" />
    </v-card-title>
    <v-row v-if="!showRegistrationImageUpdateDialog">
      <v-col cols="4">
        <image-dialog v-if="registrationImageFront" :src="registrationImageFront" title="Vorderseite" />
        <image-dialog v-if="registrationImageBack" :src="registrationImageBack" title="Rückseite" />
      </v-col>
      <v-col cols="8">
        <v-card flat>
          <v-text-field
            v-model="fullname"
            class="mr-4"
            disabled
            :label="$t('components.thg.ThgRegistrationForm.owner')"
            dense
            outlined
          ></v-text-field>
          <v-text-field
            v-model="firstRegistrationDate"
            class="mr-4 mt-n4"
            disabled
            :label="$t('components.thg.ThgRegistrationForm.firstregistration')"
            dense
            outlined
          ></v-text-field>
          <v-text-field
            v-model="myThg.registration.identificationnumber"
            class="mr-4 mt-n4"
            disabled
            :label="$t('components.thg.ThgRegistrationForm.identificationnumber')"
            dense
            outlined
          ></v-text-field>
          <v-text-field
            v-model="myThg.registration.driveTyp"
            class="mr-4 mt-n4"
            disabled
            :label="$t('components.thg.ThgRegistrationForm.driveTyp')"
            dense
            outlined
          ></v-text-field>
          <v-text-field
            v-model="myThg.registration.vehicleClass"
            class="mr-4 mt-n4"
            disabled
            :label="$t('components.thg.ThgRegistrationForm.vehicleClass')"
            dense
            outlined
          ></v-text-field>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="mx-4 my-2">
        <thg-quote-update-my-registration-image-dialog :bigButton="true" :myThg="myThg" />
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts">
import ThgQuoteUpdateMyRegistrationImageDialog from "@/components/thg/ThgQuoteUpdateMyRegistrationImageDialog.vue";
import ThgQuoteVehicleUpdateRegistrationDialog from "@/components/thg/ThgQuoteVehicleUpdateRegistrationDialog.vue";
import ImageDialog from "@/components/utility/ImageDialog.vue";
import { IThgStatus } from "@/lib/interfaces/thg/thg-status.interface";
import { simpleDate } from "@/lib/utility/date-helper";
import { thgStatusMap } from "@/lib/utility/thgStatusMap";
import { ReportImageType } from "@/models/Report/ReportImageType";
import { ThgThgMeViewModelGen } from "@/services/thg/v1/data-contracts";
import { ThgStatusEnum } from "@/store/enum/thg/thg-status.enum";
import { ThgCreateModule } from "@/store/modules/thg.create.store";
import { ImpactTypeEnum } from "@/views/thg/enum/impact-type.enum";
import { Component, Prop, Vue } from "vue-property-decorator";
import ThgQuickCheckout from "./ThgQuickCheckout.vue";
import ThgEmissionCalculationDialog from "./ThgEmissionCalculationDialog.vue";
import { requestEmissionCalculationData } from "@/lib/thg/atStatiDataRequest";
import { ThgBatchDetailViewmodelGen } from "@/services/thg/v1/data-contracts";

@Component({
  components: {
    ThgQuoteVehicleUpdateRegistrationDialog,
    ThgQuoteUpdateMyRegistrationImageDialog,
    ImageDialog,
    ThgQuickCheckout,
    ThgEmissionCalculationDialog
  }
})
export default class ThgVehicleCard extends Vue {
  @Prop()
  myThg!: ThgThgMeViewModelGen;

  @Prop({})
  batch?: ThgBatchDetailViewmodelGen;

  thgStatusMap = thgStatusMap;

  requestEmissionCalculationData(thgStatusEnum: any): boolean {
    return requestEmissionCalculationData(thgStatusEnum);
  }

  thgStatus(thgStatusEnum: any): IThgStatus {
    return (
      this.thgStatusMap.get(thgStatusEnum) || {
        text: "components.thg.ThgStatusMap.UNDEFINED.text",
        textAlt: "components.thg.ThgStatusMap.UNDEFINED.textAlt",
        icon: "mdi-help",
        portalText: "components.thg.ThgStatusMap.UNDEFINED.portalText",
        color: "error"
      }
    );
  }

  get promotionValue() {
    return this.myThg?.promotionConfiguration?.value?.toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR"
    });
  }

  get showRegistrationUpdateDialog() {
    return [ThgStatusEnum.CREATED].includes(this.myThg.status as ThgStatusEnum);
  }

  get showRegistrationImageUpdateDialog() {
    if ([ThgStatusEnum.REQUEST_REGISTRATION_IMAGES].includes(this.myThg.status as any)) {
      return true;
    } else {
      return false;
    }
  }

  get cashImpactInvestment() {
    return this.myThg.impactFactor;
  }

  get fullname() {
    let divider = "";
    if (this.myThg.registration.firstname) {
      divider = " ";
    }
    return ((this.myThg.registration.firstname as any) + divider + this.myThg.registration.name) as any;
  }

  get customerAccount() {
    return this.myThg.impactType === (ImpactTypeEnum.customerAccount as any);
  }

  get firstRegistrationDate() {
    let divider = "";
    if (this.myThg.registration.firstregistrationDay) {
      divider = ".";
    }
    return (
      (((this.myThg.registration.firstregistrationDay as any) +
        divider +
        this.myThg.registration.firstregistrationMonth) as any) +
      divider +
      this.myThg.registration.firstregistrationYear
    );
  }

  get cashPayment() {
    return 100 - this.myThg.impactFactor;
  }

  get registrationImageFront(): string {
    const item = this.myThg.registrationImages.filter((item) => item.type === (ReportImageType.registration as any));
    if (item.length !== 0) {
      return item[0].url || "";
    }
    return "";
  }

  get dateSimple() {
    const timestamp = this.myThg?.timestamp as any;

    if (timestamp?.created) {
      const locale = this.$t("utility.toLocalDateString").toString() || "de-de";
      return simpleDate(timestamp?.created, locale);
    }
    return "";
  }

  get decommissionedDateSimple() {
    if (this.myThg?.decommissioningDate) {
      const locale = this.$t("utility.toLocalDateString").toString() || "de-de";
      return simpleDate(this.myThg?.decommissioningDate, locale);
    }
    return "";
  }

  get registrationImageBack(): string {
    const item = this.myThg.registrationImages.filter(
      (item) => item.type === (ReportImageType.registrationBack as any)
    );
    if (item.length !== 0) {
      return item[0].url || "";
    }
    return "";
  }

  get isQuickCheckoutEnabled() {
    return true;
  }

  thgImpactTyeItemByEnum(impactType: any) {
    return ThgCreateModule.impactTypeItems.filter((item) => item.impactType === impactType)[0];
  }

  thgImpactImage(impactType: any) {
    return this.thgImpactTyeItemByEnum(impactType);
  }

  openRegistrationImage(imageLink: string) {
    window.open(imageLink, "_blank");
  }

  refresh() {
    this.$emit("refresh");
  }
}
</script>

<style>
.form {
  margin-top: 20px;
}
</style>
